import { useRef } from 'react';
import { LEFT, type SwipeableHandlers, type SwipeEventData, useSwipeable } from 'react-swipeable';

/**
 * Swiping left/right on the image steps through the images.
 *
 * The fade-to-black that used to accompany a swipe is gone. It set `--imageOpacity` on the
 * viewer, but `.swipeWindow` declares `--imageOpacity: 1` on itself and sits between the
 * viewer and the <img> that reads it, so the inherited value was always shadowed and the
 * fade never actually happened. Its implementation also scheduled a one-second setTimeout
 * on every single swipe-move event, none of them cleared, each touching a ref that may be
 * gone by the time it fires.
 */
export function useImageSwipe(
    onStep: (direction: 'next' | 'previous') => void,
    /** Off while the image is zoomed: the same drag pans it instead. */
    enabled = true
): SwipeableHandlers {
    // Read at the end of the gesture, which may have started before the zoom changed.
    const enabledRef = useRef(enabled);
    enabledRef.current = enabled;

    const onSwiped = (eventData: SwipeEventData) => {
        if (!enabledRef.current) {
            return;
        }
        onStep(eventData.dir === LEFT ? 'next' : 'previous');
    };

    return useSwipeable({
        onSwipedLeft: onSwiped,
        onSwipedRight: onSwiped,

        delta: 10, // min distance(px) before a swipe starts
        preventScrollOnSwipe: false,
        trackTouch: true,
        trackMouse: true,
        rotationAngle: 0,
        swipeDuration: Number.POSITIVE_INFINITY,
        touchEventOptions: { passive: true },
    });
}
